import type { Property, Restaurant, Amenity, Offer, OfferCategory } from '../types/hotel';

export const property: Property = {
  id: 'leela-palace-udaipur',
  name: 'Leela Palace Udaipur',
  location: 'Udaipur, Rajasthan, India',
  description: 'Experience unparalleled luxury at The Leela Palace Udaipur, where royal Rajasthani heritage meets contemporary elegance on the banks of Lake Pichola.',
};

export const restaurants: Restaurant[] = [
  {
    id: 'sheesh-mahal',
    name: 'Sheesh Mahal',
    cuisine: 'Indian Fine Dining',
    description: 'A taste of royalty beneath the stars overlooking Lake Pichola. Time-honored recipes from royal kitchens create culinary masterpieces like Dal-e-Sheesh and Raan-e-Sheesh. Set across two levels with magical al fresco dining.',
    images: [
      'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1550966871-3ed3c47e2ce2?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=450&fit=crop',
    ],
  },
  {
    id: 'dining-room',
    name: 'The Dining Room',
    cuisine: 'World Cuisine',
    description: 'All-day fine dining restaurant offering the finest delicacies from around the world. Features a climate-controlled wine cellar with rare and vintage collections, overlooking Lake Pichola and the outer courtyard.',
    images: [
      'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&h=450&fit=crop',
    ],
  },
  {
    id: 'library-bar',
    name: 'The Library Bar',
    cuisine: 'Bar & Lounge',
    description: 'A royal era gem with dark wood panelling, fireplaces, and fine art. Features a vast collection of single malts and cognacs including Aberfeldy, Glenfiddich, and Remy Martin. Enjoy Rajasthani folk music in the inner courtyard.',
    images: [
      'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&h=450&fit=crop',
      'https://images.unsplash.com/photo-1574096079513-d8259312b785?w=800&h=450&fit=crop',
    ],
  },
];

export const roomAmenities: Amenity[] = [
  {
    id: 'mini-bar',
    name: 'Mini Bar',
    tagline: 'Refresh. Indulge.',
    description: 'Premium beverages and snacks replenished daily.',
    image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&h=300&fit=crop',
    icon: 'Wine',
  },
  {
    id: 'iron-board',
    name: 'Iron Board',
    tagline: 'Stay Sharp. Stay Ready.',
    description: 'Full-size ironing board and premium iron.',
    image: 'https://images.unsplash.com/photo-1582738411706-bfc8e691d1c2?w=400&h=300&fit=crop',
    icon: 'Shirt',
  },
  {
    id: 'tea-coffee',
    name: 'Tea/Coffee',
    tagline: 'Wake Up. Unwind.',
    description: 'Premium tea selection and Nespresso machine.',
    image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=300&fit=crop',
    icon: 'Coffee',
  },
  {
    id: 'safe',
    name: 'Safe',
    tagline: 'Secure. Protected.',
    description: 'Electronic safe for your valuables.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=300&fit=crop',
    icon: 'Lock',
  },
  {
    id: 'laundry-bags',
    name: 'Laundry Bags',
    tagline: 'Fresh. Clean. Crisp.',
    description: 'Laundry and dry cleaning services available.',
    image: 'https://images.unsplash.com/photo-1517677208171-0bc12dd5b043?w=400&h=300&fit=crop',
    icon: 'ShoppingBag',
  },
  {
    id: 'robes',
    name: 'Robes',
    tagline: 'Comfort. Luxury.',
    description: 'Plush cotton robes and slippers.',
    image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400&h=300&fit=crop',
    icon: 'User',
  },
  {
    id: 'slippers',
    name: 'Slippers',
    tagline: 'Soft. Comfortable.',
    description: 'Premium disposable slippers.',
    image: 'https://images.unsplash.com/photo-1562183241-b937e95585b6?w=400&h=300&fit=crop',
    icon: 'Footprints',
  },
  {
    id: 'towels',
    name: 'Towels',
    tagline: 'Fluffy. Absorbent.',
    description: 'Luxury Turkish cotton towels.',
    image: 'https://images.unsplash.com/photo-1616627561839-074385245ff6?w=400&h=300&fit=crop',
    icon: 'Droplets',
  },
];

export const propertyAmenities: Amenity[] = [
  {
    id: 'spa',
    name: 'Spa',
    tagline: 'Rejuvenate. Refresh.',
    description: 'Luxurious lakeside spa offering traditional Indian therapies and modern wellness treatments. Skilled therapists provide rejuvenating experiences with stunning views of Lake Pichola.',
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&h=400&fit=crop',
    icon: 'Sparkles',
  },
  {
    id: 'swimming-pool',
    name: 'Swimming Pool',
    tagline: 'Rejuvenate. Refresh.',
    description: 'Temperature-controlled outdoor pool with stunning views of Lake Pichola and the Aravalli hills.',
    image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=1200&h=400&fit=crop',
    icon: 'Waves',
  },
  {
    id: 'conference-rooms',
    name: 'Conference Rooms',
    tagline: 'Meet. Collaborate.',
    description: 'State-of-the-art meeting facilities for business events with lakeside views.',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=400&fit=crop',
    icon: 'Users',
  },
  {
    id: 'guava-garden',
    name: 'Guava Garden',
    tagline: 'Serene. Tranquil.',
    description: 'A fragrant orchard with marble pathways winding through the palace grounds, offering a peaceful retreat.',
    image: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1200&h=400&fit=crop',
    icon: 'Trees',
  },
  {
    id: 'shiva-temple',
    name: '16th Century Shiva Temple',
    tagline: 'Sacred. Divine.',
    description: 'Experience daily sacred prayers at the historic Shiva temple within the palace grounds.',
    image: 'https://www.theleela.com/prod/content/assets/aio-banner/dekstop/Eklingji-Temple-Udaipur.jpg?VersionId=J_qhXomGO4h8aUJgRk.i_JqupvA5LmfD',
    icon: 'Landmark',
  },
];

export const offerCategories: OfferCategory[] = [
  { id: 'beauty-spa', name: 'Beauty & Spa', icon: 'Sparkles', dealCount: 5 },
  { id: 'fitness', name: 'Fitness', icon: 'Dumbbell', dealCount: 0 },
  { id: 'dine-in', name: 'Dine-In', icon: 'Utensils', dealCount: 5 },
  { id: 'stay', name: 'Stay', icon: 'Bed', dealCount: 0 },
];

export const hotDeals: Offer[] = [
  {
    id: 'deal-1',
    title: 'Festive Getaway',
    category: 'stay',
    discount: 'Special Package',
    image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=300&fit=crop',
  },
  {
    id: 'deal-2',
    title: 'Time Travel with The Leela',
    category: 'dine-in',
    discount: '15% Off Spa',
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop',
  },
  {
    id: 'deal-3',
    title: 'The Leela Palace Trail',
    category: 'stay',
    discount: 'Multi-Palace',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop',
  },
  {
    id: 'deal-4',
    title: 'Royal Meetings',
    category: 'stay',
    discount: 'Corporate',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop',
  },
];

// Hero images for pages
export const heroImages = {
  home: 'https://www.theleela.com/prod/content/assets/aio-banner/dekstop/Hero-image-leela-palace-udaipur.webp?VersionId=Gn0v91tWnFWPl21Hj7wglTi7KipN15SQ',
  roomControls: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1200&h=600&fit=crop',
  roomGuide: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&h=600&fit=crop',
  dineIn: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=600&fit=crop',
  propertyInfo: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=1200&h=600&fit=crop',
  offers: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=1200&h=600&fit=crop',
  settings: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=1200&h=600&fit=crop',
};
